import { useEffect, useState, type ChangeEvent } from "react";

import { useAuth } from "src/hooks/use-auth";
import { useRouter } from "src/hooks/use-router";
import { slackHook } from "src/utils/snug/slackHooks";
import { useV3Create, GenericPath } from "src/utils/snug/estateAppApi";
import type { ProfessionalGroup } from "src/types/snugtotal";
import type { AuthContextType } from "src/contexts/auth/jwt-context";
import { SignupStepper } from "src/sections/starter-pay/signup-stepper";
import {
  readStarterPayContext,
  type StarterPayContext,
} from "src/sections/starter-pay/context";
import "./starter-pay.css";

const isGetSnugDomain =
  window.location.hostname.endsWith("getsnug.com") ||
  window.location.hostname === "localhost";

function LockIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect
        x="3"
        y="7"
        width="10"
        height="7"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M5.5 7V5a2.5 2.5 0 015 0v2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function StarterPayAccountPage() {
  const router = useRouter();
  const { signUp } = useAuth<AuthContextType>();
  const handleAddProGroup = useV3Create<ProfessionalGroup>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3,
  );

  const [context, setContext] = useState<StarterPayContext | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const ctx = readStarterPayContext();
    if (ctx) {
      setContext(ctx);
      setHydrated(true);
      return;
    }
    // Preview escape-hatch: `?preview=1` lets us view the page without coming
    // from a real Payment step. Submit will still call the live signUp API,
    // so don't click Continue while previewing unless you mean it.
    if (new URLSearchParams(window.location.search).get("preview") === "1") {
      setContext({
        name: "Preview User",
        company: "Preview Firm",
        email: "preview@example.com",
        plan: "annual",
        planLabel: "Starter",
        paymentSchedule: "single",
      });
      setHydrated(true);
      return;
    }
    router.replace("/starter-pay");
  }, [router]);

  if (!isGetSnugDomain) return null;
  if (!hydrated || !context) return null;

  const validate = (): string | null => {
    if (password.length < 8) {
      return "Password must be at least 8 characters.";
    }
    if (password !== confirmPassword) {
      return "Passwords do not match.";
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const userSignup = await signUp(context.email, password, context.name);
      const ud_id = userSignup?.userData?.ud_id || "";
      if (!ud_id) {
        throw new Error("Account created but could not be linked to your firm. Please contact support.");
      }
      await handleAddProGroup(
        { entity: "pro-group", ud_id },
        { name: context.company },
      );

      slackHook(
        `✅ STARTER PAY — Account created\n` +
          `Name: ${context.name}\n` +
          `Email: ${context.email}\n` +
          `Company: ${context.company}\n` +
          `Plan: ${context.planLabel} (${context.paymentSchedule})`,
      );

      // Context stays in sessionStorage so the Onboarding step can read the
      // user's name/email for Calendly prefill + slack messaging. It's cleared
      // once the user picks a path on the Onboarding step.
      router.push("/starter-pay-onboarding");
    } catch (e: any) {
      const message =
        e?.response?.data?.message ||
        e?.message ||
        "Could not create your account. Please try again.";
      slackHook(
        `❌ STARTER PAY — Account creation failed\n` +
          `Email: ${context.email}\n` +
          `Error: ${message}`,
      );
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sp-page">
      <div className="sp-brand">
        <img
          src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/snug_logo.png"
          alt="Snug"
          className="sp-brand-logo"
        />
      </div>

      <section className="sp-inner">
        <h1 className="sp-headline">
          Get started with <em>Snug.</em>
        </h1>

        <p className="sp-sub">
          Includes full platform access, white-label setup, and onboarding
          support.
        </p>

        <SignupStepper currentStep="account" />

        <div className="sp-card">
          <div className="sp-card-head">
            <h2 className="sp-section-title">Secure your account</h2>
            <p className="sp-section-help">
              Choose a password of at least 8 characters. You'll use it to sign
              in with your email going forward.
            </p>
          </div>
          <div className="sp-input-grid sp-input-grid-1">
            <label className="sp-input is-locked">
              <span className="sp-input-label">Email</span>
              <input
                type="email"
                className="sp-input-field"
                value={context.email}
                disabled
                readOnly
                autoComplete="username"
              />
            </label>
            <label className="sp-input">
              <span className="sp-input-label">Password</span>
              <input
                type="password"
                className="sp-input-field"
                value={password}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                placeholder="At least 8 characters"
                autoFocus
                autoComplete="new-password"
              />
            </label>
            <label className="sp-input">
              <span className="sp-input-label">Confirm password</span>
              <input
                type="password"
                className="sp-input-field"
                value={confirmPassword}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setConfirmPassword(e.target.value);
                  setError("");
                }}
                placeholder="Re-enter your password"
                autoComplete="new-password"
              />
            </label>
          </div>
        </div>

        {error && <div className="sp-error">{error}</div>}

        <button
          type="button"
          className="sp-cta"
          disabled={loading}
          onClick={handleSubmit}
        >
          {loading ? (
            <span className="sp-cta-spinner" aria-hidden="true" />
          ) : (
            <LockIcon />
          )}
          Create my account
        </button>

        <p className="sp-disclaimer">
          Your password is encrypted in transit and at rest. We never store it
          in plain text.
        </p>
      </section>
    </div>
  );
}
